/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * SupportChatProvider — holds the state described in supportChat.ts.
 *
 * Kept in its own file, separate from the context object and the hook, so this
 * module exports a component and nothing else. A file that exports both a
 * component and a plain function defeats fast refresh, which is what the
 * react-refresh lint rule reports.
 */

import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  SupportChatContext,
  type SupportChatContextValue,
} from "./supportChat";

export const SupportChatProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedProduct, setSelectedProduct] = useState("");
  const [chatAvailable, setChatAvailable] = useState(false);
  const openChatRef = useRef<(() => void) | null>(null);

  const registerOpenChat = useCallback((fn: (() => void) | null) => {
    openChatRef.current = fn;
    setChatAvailable(fn !== null);
  }, []);

  const openChat = useCallback(() => {
    openChatRef.current?.();
  }, []);

  const value = useMemo<SupportChatContextValue>(
    () => ({
      selectedProduct,
      setSelectedProduct,
      openChat,
      chatAvailable,
      registerOpenChat,
    }),
    [selectedProduct, openChat, chatAvailable, registerOpenChat],
  );

  return (
    <SupportChatContext.Provider value={value}>
      {children}
    </SupportChatContext.Provider>
  );
};

SupportChatProvider.displayName = "SupportChatProvider";

export default SupportChatProvider;
